/**
 * Pure helpers of the Animator window's live preview: which state a node /
 * transition endpoint plays, slider ranges for parameters, the transition
 * preview timeline (Unity's transition inspector preview) and a small camera
 * math kit for the preview viewport. No React / engine imports (unit tested).
 */
import { allStates, machineAt, type BlendTree, type ControllerDoc, type Layer, type Machine, type Motion, type Transition } from './controller-doc.js';

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function machineDefault(m: Machine, path: string[]): string | null {
  const name = m.defaultState ?? m.states[0]?.name ?? m.subStateMachines?.[0]?.name;
  if (name === undefined) return null;
  if (m.states.some((s) => s.name === name)) return [...path, name].join('/');
  const sub = m.subStateMachines?.find((s) => s.name === name);
  return sub ? machineDefault(sub, [...path, name]) : null;
}

/**
 * The state a full state / sub-state machine path plays (machines enter their
 * default state, recursively). `@any`, `''` or unknown names give the layer's
 * default state.
 */
export function enteredState(layer: Layer, full: string): string | null {
  const states = allStates(layer);
  if (states.some((s) => s.full === full)) return full;
  if (full && full !== '@any') {
    const m = machineAt(layer, full.split('/'));
    if (m) return machineDefault(m, full.split('/'));
    const short = states.filter((s) => s.full.split('/').pop() === full);
    if (short.length === 1) return short[0]!.full;
  }
  return machineDefault(layer, []);
}

// ---------------------------------------------------------------------------
// Parameters
// ---------------------------------------------------------------------------

export type PreviewValue = number | boolean;

/** Initial preview values: the controller's defaults. */
export function defaultValues(doc: ControllerDoc): Record<string, PreviewValue> {
  const out: Record<string, PreviewValue> = {};
  for (const p of doc.parameters) {
    if (p.type === 'bool' || p.type === 'trigger') out[p.name] = p.type === 'bool' && p.default === true;
    else out[p.name] = typeof p.default === 'number' ? p.default : 0;
  }
  return out;
}

function treeValues(m: Motion | undefined, name: string, out: number[]) {
  if (!m || typeof m === 'string') return;
  const t: BlendTree = m;
  for (const c of t.children) {
    if (t.parameter === name) {
      if (t.type === '1d') out.push(c.threshold ?? 0);
      else if (t.type !== 'direct') out.push(c.position?.[0] ?? 0);
    }
    if (t.type !== '1d' && t.type !== 'direct' && t.parameterY === name) out.push(c.position?.[1] ?? 0);
    if (t.type === 'direct' && c.directParameter === name) out.push(0, 1);
    treeValues(c.motion, name, out);
  }
}

/**
 * Slider range of a float / int parameter: the span of the blend tree
 * thresholds / positions it drives and the transition condition values it is
 * compared with (plus its default), widened to at least 0..1.
 */
export function paramRange(doc: ControllerDoc, name: string): [number, number] {
  const values: number[] = [0, 1];
  const p = doc.parameters.find((q) => q.name === name);
  if (typeof p?.default === 'number') values.push(p.default);
  for (const layer of doc.layers) {
    for (const s of allStates(layer)) {
      treeValues(s.state.motion, name, values);
      for (const t of s.state.transitions ?? []) for (const c of t.conditions ?? []) if (c.param === name && typeof c.value === 'number') values.push(c.value);
    }
    for (const t of [...(layer.transitions ?? []), ...(layer.anyStateTransitions ?? [])])
      for (const c of t.conditions ?? []) if (c.param === name && typeof c.value === 'number') values.push(c.value);
  }
  const finite = values.filter(Number.isFinite);
  let lo = Math.min(...finite);
  let hi = Math.max(...finite);
  // Condition values sit on a boundary: leave room on both sides of them.
  const pad = (hi - lo) * 0.1;
  if (lo < 0) lo -= pad;
  hi += pad;
  return p?.type === 'int' ? [Math.floor(lo), Math.ceil(hi)] : [round(lo), round(hi)];
}

const round = (v: number) => Math.round(v * 1000) / 1000;

// ---------------------------------------------------------------------------
// Transition timeline
// ---------------------------------------------------------------------------

export interface StateTiming {
  /** Cycle length in seconds (0 = unknown; treated as 1 s like the runtime). */
  length: number;
  speed: number;
  loop: boolean;
}

export interface TransitionTimeline {
  /** Seconds the transition starts / ends on the timeline. */
  start: number;
  end: number;
  /** Timeline length (source lead-in, blend, destination tail). */
  total: number;
  /** Seconds the source / destination occupy (bars). */
  sourceEnd: number;
  /** Blend duration in seconds. */
  duration: number;
  /** One source cycle in seconds (for exit-time ticks). */
  sourceCycle: number;
}

const cycle = (s: StateTiming) => (s.length > 1e-4 ? s.length : 1);
const rate = (s: StateTiming) => (Number.isFinite(s.speed) ? s.speed : 1) / cycle(s);

/**
 * Timeline of a transition preview, mirroring the runtime: the source plays
 * from 0; the blend starts at the exit time (half a cycle when the transition
 * has none) and lasts `duration` seconds (or that fraction of the source
 * length); the destination starts at `offset` when the blend starts.
 */
export function transitionTimeline(t: Transition, src: StateTiming, dst: StateTiming): TransitionTimeline {
  const sourceCycle = cycle(src) / Math.max(Math.abs(Number.isFinite(src.speed) ? src.speed : 1), 1e-3);
  const exit = t.hasExitTime ? Math.max(0, t.exitTime ?? 1) : 0.5;
  const start = exit * sourceCycle;
  const raw = Math.max(0, t.duration ?? 0.25);
  const duration = t.fixedDuration === false ? raw * cycle(src) : raw;
  const end = start + duration;
  const dstCycle = cycle(dst) / Math.max(Math.abs(Number.isFinite(dst.speed) ? dst.speed : 1), 1e-3);
  const tail = Math.max(0.25, Math.min(dstCycle, 2) * 0.5);
  return { start, end, total: end + tail, sourceEnd: end, duration, sourceCycle };
}

/** Normalized source / destination times and destination weight at `time` seconds. */
export function transitionSample(t: Transition, src: StateTiming, dst: StateTiming, line: TransitionTimeline, time: number) {
  const fromTime = Math.max(0, time) * rate(src);
  const into = Math.max(0, time - line.start);
  const toTime = (t.offset ?? 0) + into * rate(dst);
  const weight = time < line.start ? 0 : line.duration > 0 ? Math.min(1, into / line.duration) : 1;
  return { fromTime, toTime, weight };
}

/** Normalized time of a single-state preview after `seconds` (loop wraps, else clamps). */
export function stateNormalized(timing: StateTiming, seconds: number, loop: boolean): number {
  const n = seconds * rate(timing);
  return loop ? n - Math.floor(n) : Math.min(1, Math.max(0, n));
}

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

export type Vec3 = [number, number, number];
export interface OrbitCamera {
  yaw: number;
  pitch: number;
  distance: number;
  target: Vec3;
}

/** Column-major view-projection matrix and eye position of an orbit camera. */
export function cameraMatrix(c: OrbitCamera, aspect: number, fov = 0.8): { matrix: Float32Array; eye: Vec3 } {
  const cp = Math.cos(c.pitch);
  const eye: Vec3 = [
    c.target[0] + c.distance * cp * Math.sin(c.yaw),
    c.target[1] + c.distance * Math.sin(c.pitch),
    c.target[2] + c.distance * cp * Math.cos(c.yaw),
  ];
  const f = normalize(sub(c.target, eye));
  const s = normalize(cross(f, [0, 1, 0]));
  const u = cross(s, f);
  const near = Math.max(c.distance * 0.01, 0.001);
  const far = c.distance * 100 + 10;
  const k = 1 / Math.tan(fov / 2);
  const a = (far + near) / (near - far);
  const b = (2 * far * near) / (near - far);
  // view (rows s, u, -f) then perspective.
  const view = [s[0], u[0], -f[0], 0, s[1], u[1], -f[1], 0, s[2], u[2], -f[2], 0, -dot(s, eye), -dot(u, eye), dot(f, eye), 1];
  const proj = [k / Math.max(aspect, 1e-6), 0, 0, 0, 0, k, 0, 0, 0, 0, a, -1, 0, 0, b, 0];
  return { matrix: multiply(proj, view), eye };
}

/** Projects a world point to CSS pixels (null behind the camera). */
export function projectPoint(m: ArrayLike<number>, p: Vec3, w: number, h: number): [number, number] | null {
  const x = m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!;
  const y = m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!;
  const wv = m[3]! * p[0] + m[7]! * p[1] + m[11]! * p[2] + m[15]!;
  if (wv <= 1e-6) return null;
  return [((x / wv + 1) / 2) * w, ((1 - y / wv) / 2) * h];
}

/** Camera framing an axis-aligned bounding box from the front-right. */
export function frameBounds(min: Vec3, max: Vec3, fov = 0.8): OrbitCamera {
  const target: Vec3 = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
  const radius = Math.max(0.05, Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]) / 2);
  return { yaw: 0.5, pitch: 0.18, distance: (radius / Math.sin(fov / 2)) * 1.05, target };
}

export function bounds(points: ArrayLike<number>): { min: Vec3; max: Vec3 } | null {
  if (points.length < 3) return null;
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 2 < points.length; i += 3)
    for (let k = 0; k < 3; k++) {
      const v = points[i + k]!;
      if (v < min[k]!) min[k] = v;
      if (v > max[k]!) max[k] = v;
    }
  return { min, max };
}

function multiply(a: number[], b: number[]): Float32Array {
  const out = new Float32Array(16);
  for (let c = 0; c < 4; c++)
    for (let r = 0; r < 4; r++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += a[k * 4 + r]! * b[c * 4 + k]!;
      out[c * 4 + r] = v;
    }
  return out;
}
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
function normalize(a: Vec3): Vec3 {
  const l = Math.hypot(...a) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}

/** Preview model remembered per controller (localStorage). */
export const previewModelKey = (projectId: number | string, controller: string) => `prism.animator.previewModel:${projectId}:${controller}`;

/** A sensible default preview model: a GLB the controller's clips come from, else the first model. */
export function guessModel(doc: ControllerDoc, models: readonly string[]): string | null {
  const refs: string[] = [];
  const walk = (m: Motion | undefined) => {
    if (!m) return;
    if (typeof m === 'string') refs.push(m.replace(/^gltf:/, '').split('#')[0]!.trim());
    else for (const c of m.children) walk(c.motion);
  };
  for (const l of doc.layers) for (const s of allStates(l)) walk(s.state.motion);
  return refs.find((r) => models.includes(r)) ?? models[0] ?? null;
}

/**
 * Preview values kept in step with the parameter list: new parameters start
 * at their default, removed ones are dropped, retyped ones reset.
 */
export function syncValues(doc: ControllerDoc, current: Record<string, PreviewValue>): Record<string, PreviewValue> {
  const defaults = defaultValues(doc);
  const out: Record<string, PreviewValue> = {};
  let same = Object.keys(current).length === doc.parameters.length;
  for (const p of doc.parameters) {
    const v = current[p.name];
    const fits = p.type === 'bool' || p.type === 'trigger' ? typeof v === 'boolean' : typeof v === 'number';
    out[p.name] = fits ? v! : defaults[p.name]!;
    if (!fits) same = false;
  }
  return same ? current : out;
}
